<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { title: 'Growth Record List', href: '/growth-record', color: 'primary' },
          { title: `${childName}'s Growth Record` },
        ]"
      ></v-breadcrumbs>
    </div>
    <page-title :value="$t('growthRecord.childRecordTitle', { name: childName })" />
    <div class="my-4" style="width: 200px;">
      <v-text-field 
        variant="outlined"
        density="compact"
        v-model.number="year" :suffix="$t('growthRecord.fiscalYearSuffix')"
        type="number"
      />
    </div>

    <div v-if="recordRequests.length > 0" class="py-2">
      <v-btn color="primary" variant="flat" :disabled="isSaving" @click="saveRecords">{{ $t('common.save') }}</v-btn>
    </div>
    <div v-if="isSaving" class="my-2">
      <v-progress-linear indeterminate color="primary" />
    </div>
    
    <div class="children-table">
      <div class="children-table__headers">
        <div class="children-table__header col-month">{{ $t('growthRecord.monthLabel') }}</div>
        <div class="children-table__header col-height">{{ $t('growthRecord.heightCm') }}</div>
        <div class="children-table__header col-weight">{{ $t('growthRecord.weightKg') }}</div>
        <div class="children-table__header col-other">{{ $t('growthRecord.remarks') }}</div>
      </div>
      <child-growth-record-row
        v-for="r in recordsWithMonth"
        :key="r.month"
        :record="r.record"
        :month="r.month"
        @inputHeight="inputHeight($event, r)"
        @inputWeight="inputWeight($event, r)"
        @inputRemarks="inputRemarks($event, r)"
      />
    </div>
    <div v-if="growthRecord && growthRecord.records.length > 0">
      <div>
        <g-chart
          type="ComboChart"
          :data="heightChartData"
          :options="{
            colors:['transparent', colorChartArea, colorPrimary],
            seriesType: 'area',
            isStacked: true,
            series: {
              0: { lineWidth: 0, enableInteractivity: false, visibleInLegend: false },
              1: { lineWidth: 0, enableInteractivity: false, labelInLegend: $t('growthRecord.percentileRangeLegend') },
              2: { lineWidth: 2, pointSize: 6, type: 'line' },
            },
            height: 700
          }"
        />
      </div>
      <div>
        <g-chart
          type="ComboChart"
          :data="weightChartData"
          :options="{
            colors:['transparent', colorChartArea, colorPrimary],
            seriesType: 'area',
            isStacked: true,
            series: {
              0: { lineWidth: 0, enableInteractivity: false, visibleInLegend: false },
              1: { lineWidth: 0, enableInteractivity: false, labelInLegend: $t('growthRecord.percentileRangeLegend') },
              2: { lineWidth: 2, pointSize: 6, type: 'line' },
            },
            height: 700
          }"
        />
      </div>
    </div>
    <div v-else>
      <v-card
        variant="outlined"
        class="no-chart-card"
      >
        {{ $t('growthRecord.graphsHint') }}
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import PageTitle from '@/components/atoms/PageTitle.vue';
import ChildGrowthRecordRow from '@/components/organisms/growth-record/ChildGrowthRecordRow.vue';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import { GChart } from 'vue-google-charts'

import { childGrowthRecordsModule, GrowthRepuqestWithMonth } from '@/store/viewModules/growth-record/childGrowthRecordsModule';
import { NurseryGrowthResponse, NurseryGrowthDetailResponse } from 'chaild-api/lib';

interface RecordsWithMonth {
  month: number;
  record: NurseryGrowthResponse | null;
}

interface DataType {
  isSaving: boolean;
  colorPrimary: string;
  colorChartGray: string;
  colorChartArea: string;
}

export default defineComponent({
  components: {
    PageTitle,
    ChildGrowthRecordRow,
    GChart,
  },

  data: (): DataType => ({
    isSaving: false,
    colorPrimary: '#029ec0',
    colorChartGray: '#ccc',
    colorChartArea: '#67c4d9'
  }),

  computed: {
    year: {
      get() {
        return childGrowthRecordsModule.yearInt;
      },
      set(value: number) {
        if (value > 2000 && value < 2100) {
          childGrowthRecordsModule.setYear(`${value}`)
          this.fetchGrowthRecord()
        }
      }
    },
    growthRecord() {
      return childGrowthRecordsModule.growthRecord;
    },
    recordsWithMonth(): RecordsWithMonth[] {
      const numbers = [...Array(12)].map((emp, i) => (i + 8) % 12);
      return numbers.map((m) => {
        const month = m === 0 ? 12 : m;
        if (this.growthRecord) {
          const find = this.growthRecord.records.find((r) => r.month === month);
          return {
            month,
            record: find || null,
          }
        }
        return {
          month,
          record: null,
        }
      });
    },
    childName(): string {
      if (this.growthRecord) {
        return `${this.growthRecord.child.lastName} ${this.growthRecord.child.firstName}`;
      }
      return this.$t('common.loading') as string;
    },
    recordRequests() {
      return childGrowthRecordsModule.childGrowthRecordRequests;
    },
    heightChartData() {
      if (!this.growthRecord) {
        return [];
      }

      const growthRecord = {...this.growthRecord};
      const numbers = [...Array(12)].map((emp, i) => (i + 8) % 12);
      const chartData: Array<Array<string | number | null>> = [[
        this.$t('growthRecord.monthLabel') as string,
        this.$t('growthRecord.percentileValue3') as string,
        this.$t('growthRecord.percentileValue97') as string,
        this.$t('growthRecord.height') as string,
      ]];
      
      numbers.map((m) => {
        const month = m === 0 ? 12 : m;
        const monthLabel = this.formatChartMonthLabel(month);
        const col: Array<string | number | null> = [monthLabel]
        const find = growthRecord.records.find((r) => r.month === month);
        if (find) {
          const year = month < 4 ? (this.year) + 1 : (this.year);
          const percenTiles = this.getHeightPercentile(growthRecord, year, month);
          if (percenTiles['3'] && percenTiles['97']) {
            col.push(percenTiles['3'])
            col.push(percenTiles['97'] - percenTiles['3'])
          } else {
            col.push(null)
            col.push(null)
          }

          const value = find ? find.height : null;
          col.push(value);
          chartData.push(col);
        } else {
          col.push(null)
          col.push(null)
          col.push(null)
          chartData.push(col);
        }
      });

      return chartData;
    },
    weightChartData() {
      if (!this.growthRecord) {
        return [];
      }

      const growthRecord = {...this.growthRecord};
      const numbers = [...Array(12)].map((emp, i) => (i + 8) % 12);
      const chartData: Array<Array<string | number | null>> = [[
        this.$t('growthRecord.monthLabel') as string,
        this.$t('growthRecord.percentileValue3') as string,
        this.$t('growthRecord.percentileValue97') as string,
        this.$t('growthRecord.weight') as string,
      ]];
      
      numbers.map((m) => {
        const month = m === 0 ? 12 : m;
        const monthLabel = this.formatChartMonthLabel(month);
        const col: Array<string | number | null> = [monthLabel]
        const find = growthRecord.records.find((r) => r.month === month);
        if (find) {
          const year = month < 4 ? (this.year) + 1 : (this.year);
          const percenTiles = this.getWeightPercentile(growthRecord, year, month);
          if (percenTiles['3'] && percenTiles['97']) {
            col.push(percenTiles['3'])
            col.push(percenTiles['97'] - percenTiles['3'])
          } else {
            col.push(null)
            col.push(null)
          }

          const value = find ? find.weight : null;
          col.push(value);
          chartData.push(col);
        } else {
          col.push(null)
          col.push(null)
          col.push(null)
          chartData.push(col);
        }
      });

      return chartData;
    }
  },

  methods: {
    formatChartMonthLabel(month: number): string {
      return dayjs(`${this.year}-${month}-01`).locale('en').format('MMM');
    },
    async fetchGrowthRecord() {
      this.isSaving = true
      await childGrowthRecordsModule.getGrowthRecord()
      this.isSaving = false
    },
    inputHeight(value: number, record: RecordsWithMonth) {
      if (this.growthRecord) {
        const find = this.recordRequests.find((r) => r.month === record.month);
        const newRecord: GrowthRepuqestWithMonth = find ? {...find} : { 
          month: record.month,
          request: {
            childId: this.growthRecord.child.childId,
          }
        };
        newRecord.request.height = value;
        childGrowthRecordsModule.updateChildGrowthRecordRequests(newRecord);
      }
    },
    inputWeight(value: number, record: RecordsWithMonth) {
      if (this.growthRecord) {
        const find = this.recordRequests.find((r) => r.month === record.month);
        const newRecord: GrowthRepuqestWithMonth = find ? {...find} : { 
          month: record.month,
          request: {
            childId: this.growthRecord.child.childId,
          }
        };
        newRecord.request.weight = value;
        childGrowthRecordsModule.updateChildGrowthRecordRequests(newRecord);
      }
    },
    inputRemarks(value: string, record: RecordsWithMonth) {
      if (this.growthRecord) {
        const find = this.recordRequests.find((r) => r.month === record.month);
        const newRecord: GrowthRepuqestWithMonth = find ? {...find} : { 
          month: record.month,
          request: {
            childId: this.growthRecord.child.childId,
          }
        };
        newRecord.request.remarks = value;
        childGrowthRecordsModule.updateChildGrowthRecordRequests(newRecord);
      }
    },
    async saveRecords() {
      this.isSaving = true;
      await childGrowthRecordsModule.saveRecords();
      await childGrowthRecordsModule.getGrowthRecord();
      this.isSaving = false;
    },
    getHeightPercentile(record: NurseryGrowthDetailResponse | null, year: number, month: number): { [key: number]: number | null } {
      if (record) {
        const birthDay = dayjs(record.child.dateOfBirth);
        const months = dayjs(`${year}-${month}`).diff(birthDay, 'month');
        const gender = record.child.gender;
        const percentileValues = childGrowthRecordsModule.percentileMaster.filter((pm) => {
          if (
            pm.sex === gender
            && pm.month === months
            && pm.valueType === 'height'
          ) {
            return true;
          }
          return false;
        })
        if (percentileValues.length === 7) {
          percentileValues.sort((pv1, pv2) => {
            return pv1.percentile - pv2.percentile
          })
          return {
            3: percentileValues[0].value,
            10: percentileValues[1].value,
            25: percentileValues[2].value,
            50: percentileValues[3].value,
            75: percentileValues[4].value,
            90: percentileValues[5].value,
            97: percentileValues[6].value,
          }
        }
      }
      return { 3: null, 10: null, 25: null, 50: null, 75: null, 90: null, 97: null }
    },
    getWeightPercentile(record: NurseryGrowthDetailResponse | null, year: number, month: number): { [key: number]: number | null } {
      if (record) {
        const birthDay = dayjs(record.child.dateOfBirth);
        const months = dayjs(`${year}-${month}`).diff(birthDay, 'month');
        const gender = record.child.gender;
        const percentileValues = childGrowthRecordsModule.percentileMaster.filter((pm) => {
          if (
            pm.sex === gender
            && pm.month === months
            && pm.valueType === 'weight'
          ) {
            return true;
          }
          return false;
        })
        if (percentileValues.length === 7) {
          percentileValues.sort((pv1, pv2) => {
            return pv1.percentile - pv2.percentile
          })
          return {
            3: percentileValues[0].value,
            10: percentileValues[1].value,
            25: percentileValues[2].value,
            50: percentileValues[3].value,
            75: percentileValues[4].value,
            90: percentileValues[5].value,
            97: percentileValues[6].value,
          }
        }
      }
      return { 3: null, 10: null, 25: null, 50: null, 75: null, 90: null, 97: null }
    },
  },

  mounted() {
    const childId = this.$route.params.childId;
    if (childId) {
      try {
        const childIdInt = parseInt(childId as string, 10);
        childGrowthRecordsModule.initYear();
        childGrowthRecordsModule.setChildId(childIdInt);
        childGrowthRecordsModule.getGrowthRecord();
        childGrowthRecordsModule.getPercentile();
      } catch {
        return;
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.children-table__headers {
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 8px;
  border-bottom: solid 1px #e0e0e0;
  margin-bottom: 8px;
}

.children-table__header {
  font-size: 12px;
  font-weight: bold;
}

::v-deep {
  .children-table__row {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 8px 0;
  }
  .children-table__row-col {
    padding: 4px;
    font-size: 14px;
  }
  .col-month { width: 80px; }
  .col-height { width: 120px; }
  .col-weight { width: 120px; }
  .col-other { flex: 1; }
}

.no-chart-card {
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 40px 0;
  background-color: #f5f5f5;
}
</style>